/**
 * Meeting code & link helpers.
 *
 * A meeting is identified by a persistent room code (see lib/rooms.js) or,
 * for legacy one-time links, by the host's raw peer invite code. We
 * treat the code as an opaque string and wrap it in shareable URLs of the
 * form:
 *
 *   https://example.com/<encoded-code>        (what the CRM and this app hand out)
 *   https://example.com/#/m/<encoded-code>    (the in-app route; older links)
 */

const MEETING_HASH_RE = /^#\/m\/(.+)$/;

/** Build a shareable meeting URL from a code. */
export function buildMeetingURL(code, base) {
  const origin = base ?? (typeof location !== 'undefined' ? location.origin : '');
  return `${origin.replace(/\/+$/, '')}/${encodeURIComponent(code)}`;
}

/** Paths the site itself serves: never room ids. */
const RESERVED_PATHS = new Set(['login', 'signal', 'auth', 'v1', 'healthz', 'assets', 'icons', 'backgrounds', 'models']);

/**
 * The room id in a path like `/TRT-abc123-def456` or `/discovery_call`, or null for any other path
 * (more than one segment, a file name with a dot, or a path the site itself serves).
 */
export function roomFromPath(pathname) {
  const m = /^\/([A-Za-z0-9][A-Za-z0-9_-]{1,62}[A-Za-z0-9])\/?$/.exec(pathname ?? '');
  return m && !RESERVED_PATHS.has(m[1].toLowerCase()) ? m[1] : null;
}

/** Parse a location.hash value into a route descriptor. */
export function parseHash(hash) {
  if (!hash || hash === '#' || hash === '#/') return { view: 'home' };
  const m = MEETING_HASH_RE.exec(hash);
  if (m) {
    try {
      return { view: 'meeting', code: decodeURIComponent(m[1]) };
    } catch {
      return { view: 'meeting', code: m[1] };
    }
  }
  return { view: 'home' };
}

/**
 * Extract a meeting code from arbitrary user input: a raw code, a full
 * meeting link, or a link pasted with extra whitespace.
 * Returns null when nothing usable is found.
 */
export function extractCode(input) {
  if (typeof input !== 'string') return null;
  const text = input.trim();
  if (!text) return null;

  // Full URL (or anything containing our hash route).
  const hashIdx = text.indexOf('#/m/');
  if (hashIdx !== -1) {
    const route = parseHash(text.slice(hashIdx));
    return route.view === 'meeting' && route.code ? route.code.trim() || null : null;
  }

  // A link with the room id as its path (https://meet.example.com/TRT-abc123-def456).
  if (/^https?:\/\//i.test(text)) {
    try {
      return roomFromPath(new URL(text).pathname);
    } catch {
      return null;
    }
  }

  // Otherwise treat the input as the raw invite code.
  return text;
}

/**
 * A video link into another service (Google Meet, Zoom, Teams, any https link that is not this site):
 * returns it normalised, or null when the text is not a link or the link is into this site. Such a
 * link is opened in its own tab, never read as one of our room names.
 */
export function externalLink(input, ownOrigin = typeof location !== 'undefined' ? location.origin : '') {
  if (typeof input !== 'string') return null;
  const text = input.trim();
  if (!/^https?:\/\//i.test(text) || text.length > 2000) return null;
  try {
    const url = new URL(text);
    if (url.username || url.password) return null;
    return ownOrigin && url.origin === new URL(ownOrigin).origin ? null : url.href;
  } catch {
    return null;
  }
}

/**
 * Shorten long codes for display: keeps the start and end,
 * inserts an ellipsis in the middle.
 */
export function displayCode(code, max = 24) {
  if (typeof code !== 'string') return '';
  if (code.length <= max) return code;
  const keep = Math.max(4, Math.floor((max - 1) / 2));
  return `${code.slice(0, keep)}…${code.slice(-keep)}`;
}
